import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Search, Eye, Share2, Lock } from 'lucide-react';
import axios from 'axios';
import RichTextEditor from '@/components/RichTextEditor';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContentTemplates({ user }) {
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    content_html: '',
    visibility: 'private'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/content-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    }
  };

  const handleImageUpload = async (file) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/events/upload-description-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Return relative URL so it works in any environment (preview, production, custom domain)
      return response.data.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      return null;
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.content_html) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/content-templates`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template created successfully');
      setShowCreateDialog(false);
      setFormData({ name: '', content_html: '', visibility: 'private' });
      fetchTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!formData.name || !formData.content_html) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/content-templates/${currentTemplate.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template updated successfully');
      setShowEditDialog(false);
      setCurrentTemplate(null);
      setFormData({ name: '', content_html: '', visibility: 'private' });
      fetchTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/content-templates/${template.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const openCreateDialog = () => {
    setFormData({ name: '', content_html: '', visibility: 'private' });
    setShowCreateDialog(true);
  };

  const openEditDialog = (template) => {
    setCurrentTemplate(template);
    setFormData({
      name: template.name,
      content_html: template.content_html,
      visibility: template.visibility
    });
    setShowEditDialog(true);
  };

  const openPreviewDialog = (template) => {
    setCurrentTemplate(template);
    setShowPreviewDialog(true);
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Sidebar user={user} />
      <div className="main-content">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">Content Templates</h1>
          <p className="text-slate-400">Create and manage reusable content for event descriptions</p>
        </div>

        {/* Search and Create */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-5 h-5 mr-2" />
            New Template
          </Button>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => {
            const isOwner = template.created_by === currentUser.id;
            
            return (
              <Card key={template.id} className="bg-slate-800 border-slate-700 p-4 hover:border-emerald-500 transition-all group">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-1">{template.name}</h3>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      {template.visibility === 'shared' ? (
                        <>
                          <Share2 className="w-3 h-3" />
                          <span>Shared</span>
                        </>
                      ) : (
                        <>
                          <Lock className="w-3 h-3" />
                          <span>Private</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openPreviewDialog(template)}
                      title="Preview"
                    >
                      <Eye className="w-4 h-4 text-blue-400" />
                    </Button>
                    {isOwner && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(template)}
                          title="Edit"
                        >
                          <Edit className="w-4 h-4 text-emerald-400" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(template)}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Content Preview */}
                <div 
                  className="text-sm text-slate-300 line-clamp-3"
                  dangerouslySetInnerHTML={{ 
                    __html: template.content_html.replace(/<[^>]*>/g, ' ').substring(0, 150) + '...' 
                  }}
                />
                
                <div className="mt-3 text-xs text-slate-500">
                  Created {new Date(template.created_at).toLocaleDateString()}
                </div>
              </Card>
            );
          })}
        </div>

        {filteredTemplates.length === 0 && (
          <Card className="bg-slate-800 border-slate-700 p-12 text-center">
            <p className="text-slate-400 text-lg mb-4">No templates found</p>
            <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-5 h-5 mr-2" />
              Create Your First Template
            </Button>
          </Card>
        )}

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Content Template</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Template Name</label>
                <input
                  type="text"
                  placeholder="e.g., Standard Safety Instructions"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Visibility</label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="private">Private (Only Me)</option>
                  <option value="shared">Shared (All Users)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Content</label>
                <RichTextEditor
                  content={formData.content_html}
                  onChange={(html) => setFormData({ ...formData, content_html: html })}
                  onImageUpload={handleImageUpload}
                  placeholder="Enter your template content..."
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
                  {loading ? 'Creating...' : 'Create Template'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Content Template</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Template Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Visibility</label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="private">Private (Only Me)</option>
                  <option value="shared">Shared (All Users)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Content</label>
                <RichTextEditor
                  content={formData.content_html}
                  onChange={(html) => setFormData({ ...formData, content_html: html })}
                  onImageUpload={handleImageUpload}
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleEdit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{currentTemplate?.name}</DialogTitle>
            </DialogHeader>
            <div 
              className="prose max-w-none p-4"
              dangerouslySetInnerHTML={{ __html: currentTemplate?.content_html || '' }}
            />
          </DialogContent>
        </Dialog>
        </div>
      </div>
    </div>
  );
}
